import { useEffect, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase";
import {
    Settings,
    FileText,
    Brain,
    Download,
    Plus,
    Minus,
    Sparkles,
    BookOpen,
    Target,
    Zap,
    CheckCircle,
    Clock,
    Users,
    TrendingUp,
    FileType,
    FileDown,
    Youtube,
    PlayCircle,
    User,
    Globe,
    AlertCircle
} from "lucide-react";

/* =========================
   TYPES
========================= */
export type FileFormat = "docx" | "pdf";

interface YouTubeVideoInfo {
    title: string;
    channel: string;
    thumbnail: string;
    hasTranscript: boolean;
}

/* =========================
   SUB COMPONENT - TaskTypeCard
========================= */
interface TaskTypeCardProps {
    type: string;
    label: string;
    description: string;
    value: number;
    onChange: (value: number) => void;
    color: string;
    icon: string;
}

function TaskTypeCard({ label, description, value, onChange, color, icon }: TaskTypeCardProps) {
    return (
        <div
            className="bg-gray-900/50 border-l-4 p-5 rounded-xl border border-gray-800 hover:-translate-y-1 hover:shadow-2xl hover:shadow-black/20 transition-all duration-300"
            style={{ borderLeftColor: color }}
        >
            <div className="flex items-start gap-3 mb-4">
                <span className="text-2xl">{icon}</span>
                <div className="flex-1">
                    <h4 className="text-base font-bold text-white mb-1">{label}</h4>
                    <p className="text-sm text-gray-400">{description}</p>
                </div>
            </div>

            <div className="flex items-center gap-2">
                <button
                    onClick={() => onChange(Math.max(0, value - 1))}
                    disabled={value <= 0}
                    className="w-9 h-9 rounded-lg border-2 border-gray-700 bg-gray-950/80 flex items-center justify-center text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:border-red-500 hover:text-red-500 transition-colors"
                >
                    <Minus size={16} />
                </button>
                <input
                    type="number"
                    min={0}
                    max={10}
                    value={value}
                    onChange={(e) => {
                        const val = parseInt(e.target.value);
                        if (!isNaN(val)) onChange(Math.min(10, Math.max(0, val)));
                    }}
                    className="flex-1 py-2 text-center text-lg font-bold border-2 rounded-lg bg-gray-950/80 text-white focus:outline-none focus:ring-2 focus:ring-red-500/50"
                    style={{ borderColor: color }}
                />
                <button
                    onClick={() => onChange(Math.min(10, value + 1))}
                    disabled={value >= 10}
                    className="w-9 h-9 rounded-lg border-2 border-gray-700 bg-gray-950/80 flex items-center justify-center text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:border-red-500 hover:text-red-500 transition-colors"
                >
                    <Plus size={16} />
                </button>
            </div>
        </div>
    );
}

/* =========================
   MAIN COMPONENT - YouTubeTaskCreator
========================= */
export default function YouTubeTaskCreator() {
    const [topic, setTopic] = useState("");
    const [context, setContext] = useState("");
    const [difficulty, setDifficulty] = useState("mittel");
    const [fileFormat, setFileFormat] = useState<FileFormat>("docx");
    const [youtubeUrl, setYoutubeUrl] = useState("");

    const [numSingle, setNumSingle] = useState(2);
    const [numMultiple, setNumMultiple] = useState(1);
    const [numFree, setNumFree] = useState(1);
    const [numGap, setNumGap] = useState(1);

    const [loading, setLoading] = useState(false);
    const [debugInfo, setDebugInfo] = useState<string>("");
    const [activeTab, setActiveTab] = useState<"config" | "preview" | "video">("config");

    const [videoInfo, setVideoInfo] = useState<YouTubeVideoInfo | null>(null);
    const [checkingVideo, setCheckingVideo] = useState(false);
    const [videoError, setVideoError] = useState("");

    /* =========================
       YouTube URL validieren und Video-Info holen
    ========================= */
    useEffect(() => {
        const checkYouTubeUrl = async () => {
            if (!youtubeUrl.trim()) {
                setVideoInfo(null);
                setVideoError("");
                return;
            }

            const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
            if (!youtubeRegex.test(youtubeUrl)) {
                setVideoError("Bitte eine gültige YouTube-URL eingeben");
                setVideoInfo(null);
                return;
            }

            setCheckingVideo(true);
            setVideoError("");

            try {
                const getYouTubeInfo = httpsCallable(getFunctions(app), "getYouTubeInfo");
                const result: any = await getYouTubeInfo({ youtubeUrl });

                if (result.data?.error) {
                    setVideoError(result.data.error);
                    setVideoInfo(null);
                } else if (result.data?.title) {
                    setVideoInfo({
                        title: result.data.title,
                        channel: result.data.channel || "Unbekannter Kanal",
                        thumbnail: result.data.thumbnail || "",
                        hasTranscript: result.data.hasTranscript || false
                    });
                }
            } catch (err: any) {
                setVideoError("Fehler beim Abrufen der Video-Informationen");
                console.error("Fehler:", err);
            } finally {
                setCheckingVideo(false);
            }
        };

        const timeoutId = setTimeout(checkYouTubeUrl, 1000);
        return () => clearTimeout(timeoutId);
    }, [youtubeUrl]);

    /* =========================
       Generate Tasks from YouTube
    ========================= */
    const generateTasks = async () => {
        if (!topic.trim()) {
            alert("Bitte ein Thema angeben.");
            return;
        }

        if (!youtubeUrl.trim()) {
            alert("Bitte eine YouTube-URL eingeben.");
            return;
        }

        setLoading(true);
        setDebugInfo("");

        try {
            const functionName = fileFormat === "pdf"
                ? "generateTasksFromYouTubePdf"
                : "generateTasksFromYouTube";

            const result: any = await httpsCallable(
                getFunctions(app),
                functionName
            )({
                youtubeUrl: youtubeUrl.trim(),
                topic: topic.trim(),
                context: context.trim(),
                difficulty,
                numSingle,
                numMultiple,
                numFree,
                numGap,
            });

            if (result.data?.file) {
                const mimeType = fileFormat === "pdf"
                    ? "application/pdf"
                    : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

                const blob = b64toBlob(result.data.file, mimeType);
                const url = URL.createObjectURL(blob);

                const videoTitle = videoInfo?.title || "YouTube_Video";
                const safeTitle = videoTitle.replace(/[^a-z0-9]/gi, '_');
                const topicTitle = topic.replace(/[^a-z0-9]/gi, '_');

                const a = document.createElement("a");
                a.href = url;
                a.download = `Arbeitsauftrag_${topicTitle}_${safeTitle}.${fileFormat}`;
                a.click();

                URL.revokeObjectURL(url);

                setDebugInfo(`✅ Aufgaben erfolgreich als ${fileFormat.toUpperCase()} generiert!`);
                setTimeout(() => setDebugInfo(""), 5000);
            } else if (result.data?.error) {
                setDebugInfo(`❌ Fehler: ${result.data.error}`);
            } else {
                setDebugInfo("❌ Unbekannter Fehler beim Erstellen der Datei.");
            }
        } catch (err: any) {
            console.error("Fehler:", err);
            setDebugInfo(`❌ Serverfehler: ${err?.message || JSON.stringify(err)}`);
        }

        setLoading(false);
    };

    /* =========================
       Helpers
    ========================= */
    const b64toBlob = (base64: string, type: string) => {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        return new Blob([new Uint8Array(byteNumbers)], { type });
    };

    /* =========================
       Reset zu Standardwerten
    ========================= */
    const resetToDefaults = () => {
        setTopic("");
        setYoutubeUrl("");
        setNumSingle(2);
        setNumMultiple(1);
        setNumFree(1);
        setNumGap(1);
        setDifficulty("mittel");
        setContext("");
        setFileFormat("docx");
        setVideoInfo(null);
        setVideoError("");
    };

    /* =========================
       Preview Content
    ========================= */
    const renderPreview = () => {
        const totalTasks = numSingle + numMultiple + numFree + numGap;

        return (
            <div className="bg-gray-900/30 border border-gray-800 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-6">
                    <FileText size={24} className="text-white" />
                    <h3 className="text-xl font-bold text-white">Vorschau deines Arbeitsblatts</h3>
                </div>

                <div className="space-y-6">
                    <div className="bg-gray-950/50 border border-gray-800 rounded-xl p-6">
                        <h4 className="text-lg font-bold text-white mb-2">Arbeitsauftrag: {topic || "[Thema]"}</h4>

                        {videoInfo && (
                            <div className="mb-6 p-4 bg-red-500/10 border-l-4 border-red-500 rounded-r-lg">
                                <div className="flex items-center gap-2 mb-2">
                                    <Youtube size={20} className="text-red-500" />
                                    <span className="text-red-500 font-semibold">YouTube Video</span>
                                </div>
                                <p className="text-gray-300 italic">
                                    "{videoInfo.title.substring(0, 60)}"
                                    {videoInfo.title.length > 60 && "..."}
                                </p>
                            </div>
                        )}

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            <div className="text-center">
                                <div className="text-3xl font-black text-red-500">{totalTasks}</div>
                                <div className="text-sm text-gray-400">Aufgaben</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-black text-red-500">{numSingle}</div>
                                <div className="text-sm text-gray-400">Single Choice</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-black text-red-500">{numMultiple}</div>
                                <div className="text-sm text-gray-400">Multiple Choice</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-black text-red-500">{numFree + numGap}</div>
                                <div className="text-sm text-gray-400">Freie/Lückentexte</div>
                            </div>
                        </div>

                        <div>
                            <h5 className="text-white font-semibold mb-3">Beispielaufgaben (basierend auf Video-Inhalt):</h5>
                            <div className="space-y-3">
                                {numSingle > 0 && (
                                    <div className="bg-gray-900/30 p-4 rounded-lg">
                                        <span className="inline-block bg-green-500/20 text-green-500 text-xs font-semibold px-3 py-1 rounded-full mb-2">Single Choice</span>
                                        <p className="text-gray-300">Welche Aussage aus dem Video beschreibt am besten...?</p>
                                    </div>
                                )}
                                {numGap > 0 && (
                                    <div className="bg-gray-900/30 p-4 rounded-lg">
                                        <span className="inline-block bg-yellow-500/20 text-yellow-500 text-xs font-semibold px-3 py-1 rounded-full mb-2">Lückentext</span>
                                        <p className="text-gray-300">Fülle die Lücken basierend auf dem Video-Inhalt: _____</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex items-center gap-3 text-gray-300">
                            <CheckCircle size={18} className="text-green-500" />
                            <span className="text-sm">Basierend auf YouTube-Video</span>
                        </div>
                        <div className="flex items-center gap-3 text-gray-300">
                            <Clock size={18} className="text-blue-500" />
                            <span className="text-sm">Format: {fileFormat.toUpperCase()}</span>
                        </div>
                        <div className="flex items-center gap-3 text-gray-300">
                            <Users size={18} className="text-purple-500" />
                            <span className="text-sm">Video-Transkript analysiert</span>
                        </div>
                        <div className="flex items-center gap-3 text-gray-300">
                            <TrendingUp size={18} className="text-yellow-500" />
                            <span className="text-sm">Didaktisch optimiert</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    /* =========================
       Video Content
    ========================= */
    const renderVideoInfo = () => (
        <div className="bg-gray-900/30 border border-gray-800 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-6">
                <Youtube size={24} className="text-white" />
                <h3 className="text-xl font-bold text-white">YouTube Video</h3>
            </div>

            <div className="min-h-[200px]">
                {youtubeUrl ? (
                    <>
                        {checkingVideo ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <div className="w-12 h-12 border-4 border-gray-700 border-t-red-500 rounded-full animate-spin mb-4" />
                                <p className="text-gray-400">Video wird analysiert...</p>
                            </div>
                        ) : videoError ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <AlertCircle size={48} className="text-red-500 mb-4" />
                                <p className="text-red-500 font-semibold text-center mb-2">{videoError}</p>
                                <p className="text-gray-400 text-sm text-center">Bitte überprüfe die URL oder wähle ein anderes Video.</p>
                            </div>
                        ) : videoInfo ? (
                            <>
                                <div className="border border-gray-800 rounded-xl overflow-hidden bg-gray-950/30">
                                    <div className="relative">
                                        {videoInfo.thumbnail ? (
                                            <img
                                                src={videoInfo.thumbnail}
                                                alt={videoInfo.title}
                                                className="w-full h-48 object-cover"
                                            />
                                        ) : (
                                            <div className="w-full h-48 bg-gray-800 flex items-center justify-center">
                                                <PlayCircle size={48} className="text-gray-500" />
                                            </div>
                                        )}
                                        <div className="absolute top-3 left-3 bg-red-600 text-white px-3 py-1 rounded-full flex items-center gap-2 text-sm font-semibold">
                                            <Youtube size={16} />
                                            <span>YouTube</span>
                                        </div>
                                    </div>

                                    <div className="p-5">
                                        <h4 className="text-lg font-bold text-white mb-3 line-clamp-2">{videoInfo.title}</h4>

                                        <div className="space-y-3 mb-4">
                                            <div className="flex items-center gap-2 text-gray-400">
                                                <User size={16} />
                                                <span className="text-sm">{videoInfo.channel}</span>
                                            </div>
                                            <div className="flex items-center gap-2 text-gray-400">
                                                <Globe size={16} />
                                                <span className="text-sm">{videoInfo.hasTranscript ? "Transkript verfügbar" : "Automatisches Transkript"}</span>
                                            </div>
                                        </div>

                                        <div className={`p-3 rounded-lg ${videoInfo.hasTranscript ? 'bg-green-500/10 border border-green-500/20' : 'bg-yellow-500/10 border border-yellow-500/20'}`}>
                                            <div className="flex items-center gap-2">
                                                {videoInfo.hasTranscript ? (
                                                    <>
                                                        <CheckCircle size={16} className="text-green-500" />
                                                        <span className="text-sm text-green-500">Video kann für Aufgaben verwendet werden</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <AlertCircle size={16} className="text-yellow-500" />
                                                        <span className="text-sm text-yellow-500">Verwende Video-Beschreibung für Aufgaben</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-6 bg-gray-950/30 border border-gray-800 rounded-xl p-5">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Sparkles size={20} className="text-yellow-500" />
                                        <strong className="text-white">Wie funktioniert's?</strong>
                                    </div>
                                    <ul className="space-y-2 text-gray-300">
                                        <li className="flex items-start gap-2">
                                            <span className="text-red-500">•</span>
                                            <span className="text-sm">Die KI analysiert das Video-Transkript</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span className="text-red-500">•</span>
                                            <span className="text-sm">Aufgaben werden basierend auf dem Video-Inhalt erstellt</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span className="text-red-500">•</span>
                                            <span className="text-sm">Ideal für aktuelle Themen und audiovisuelle Inhalte</span>
                                        </li>
                                        <li className="flex items-start gap-2">
                                            <span className="text-red-500">•</span>
                                            <span className="text-sm">Schüler können das Video vor den Aufgaben anschauen</span>
                                        </li>
                                    </ul>
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center py-12">
                                <Youtube size={48} className="text-gray-500 mb-4" />
                                <p className="text-gray-400 text-center">
                                    Gib eine YouTube-URL ein, um Video-Informationen zu sehen
                                </p>
                            </div>
                        )}
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center py-12">
                        <Youtube size={48} className="text-gray-500 mb-4" />
                        <p className="text-gray-400 text-center mb-6">
                            Füge eine YouTube-URL hinzu, um Aufgaben basierend auf Videos zu erstellen
                        </p>
                        <div className="bg-gray-950/30 border border-gray-800 rounded-xl p-5 w-full">
                            <p className="text-white font-semibold mb-3">Beispiel-URLs:</p>
                            <ul className="space-y-2 text-gray-300 text-sm">
                                <li className="font-mono">https://www.youtube.com/watch?v=VIDEO_ID</li>
                                <li className="font-mono">https://youtu.be/VIDEO_ID</li>
                                <li className="font-mono">https://youtube.com/shorts/VIDEO_ID</li>
                            </ul>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    /* =========================
       RENDER
    ========================= */
    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-950 font-sans relative overflow-x-hidden">
            {/* Background Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-red-500/30 to-transparent opacity-40 blur-[100px] animate-pulse-glow -z-10" />

            {/* HEADER */}
            <div className="bg-gray-900/80 backdrop-blur-lg border-b border-gray-800 relative z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex flex-col lg:flex-row lg:items-start gap-6 mb-6">
                        <div className="bg-gradient-to-br from-red-600 to-red-500 w-16 h-16 rounded-2xl flex items-center justify-center text-white flex-shrink-0">
                            <Youtube size={32} />
                        </div>
                        <div className="flex-1">
                            <div className="inline-block bg-red-500/15 text-red-500 px-4 py-2 rounded-full text-sm font-semibold mb-4 border border-red-500/30 backdrop-blur-sm">
                                🎬 YouTube Video Generator
                            </div>
                            <h1 className="text-3xl md:text-4xl font-black text-white mb-3">
                                Erstelle <span className="bg-gradient-to-r from-red-500 to-red-400 bg-clip-text text-transparent">Video-basierte</span> Arbeitsblätter
                            </h1>
                            <p className="text-gray-300 text-lg">
                                Für den Islam-Unterricht – basierend auf YouTube-Videos mit KI-Analyse
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <span className="inline-flex items-center gap-2 bg-gray-800/50 text-gray-300 px-4 py-2 rounded-full text-sm font-medium border border-gray-700">
                            <Youtube size={14} /> YouTube Integration
                        </span>
                        <span className="inline-flex items-center gap-2 bg-gray-800/50 text-gray-300 px-4 py-2 rounded-full text-sm font-medium border border-gray-700">
                            <FileType size={14} /> {fileFormat.toUpperCase()}
                        </span>
                        <span className="inline-flex items-center gap-2 bg-gray-800/50 text-gray-300 px-4 py-2 rounded-full text-sm font-medium border border-gray-700">
                            <Target size={14} /> Transkript-Analyse
                        </span>
                        <span className="inline-flex items-center gap-2 bg-gray-800/50 text-gray-300 px-4 py-2 rounded-full text-sm font-medium border border-gray-700">
                            <Zap size={14} /> KI-gestützt
                        </span>
                    </div>
                </div>
            </div>

            {/* MAIN CONTENT */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 relative z-10">
                {/* LEFT PANEL */}
                <div className="lg:col-span-2">
                    <div className="bg-gray-900/80 backdrop-blur-lg border border-gray-800 rounded-2xl overflow-hidden">
                        {/* Tabs */}
                        <div className="flex flex-wrap gap-2 p-2 bg-gray-950/50 border-b border-gray-800">
                            <button
                                className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold transition-all ${activeTab === "config" ? "bg-red-500/20 text-red-500 border border-red-500/30" : "text-gray-500 hover:text-gray-300 hover:bg-gray-800/30"}`}
                                onClick={() => setActiveTab("config")}
                            >
                                <Settings size={18} />
                                Konfiguration
                            </button>
                            <button
                                className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold transition-all ${activeTab === "preview" ? "bg-red-500/20 text-red-500 border border-red-500/30" : "text-gray-500 hover:text-gray-300 hover:bg-gray-800/30"}`}
                                onClick={() => setActiveTab("preview")}
                            >
                                <FileText size={18} />
                                Vorschau
                            </button>
                            <button
                                className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold transition-all ${activeTab === "video" ? "bg-red-500/20 text-red-500 border border-red-500/30" : "text-gray-500 hover:text-gray-300 hover:bg-gray-800/30"}`}
                                onClick={() => setActiveTab("video")}
                            >
                                <Youtube size={18} />
                                Video
                            </button>
                        </div>

                        {/* Tab Content */}
                        <div className="p-4 sm:p-6 max-h-[calc(100vh-300px)] overflow-y-auto">
                            {activeTab === "config" ? (
                                <div className="space-y-8">
                                    {/* YouTube URL */}
                                    <section>
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="bg-red-500/10 w-10 h-10 rounded-xl flex items-center justify-center text-red-500 flex-shrink-0">
                                                <Youtube size={20} />
                                            </div>
                                            <h3 className="text-xl font-bold text-white">YouTube Video</h3>
                                        </div>
                                        <div className="space-y-3">
                                            <label className="block text-sm font-semibold text-gray-300">
                                                YouTube Video URL *
                                                <span className="block text-xs text-gray-500 font-normal mt-1">
                                                    Das Video wird analysiert und als Grundlage für Aufgaben verwendet
                                                </span>
                                            </label>
                                            <input
                                                value={youtubeUrl}
                                                onChange={(e) => setYoutubeUrl(e.target.value)}
                                                placeholder="z. B. https://www.youtube.com/watch?v=..."
                                                className="w-full px-4 py-3 bg-gray-900/50 border-2 border-gray-800 rounded-xl text-white focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/30"
                                            />
                                            {videoError && (
                                                <div className="flex items-center gap-2 text-red-500 text-sm p-3 bg-red-500/10 rounded-lg">
                                                    <AlertCircle size={14} />
                                                    {videoError}
                                                </div>
                                            )}
                                        </div>

                                        {videoInfo && (
                                            <div className="mt-4 border border-gray-800 rounded-xl p-4 bg-gray-950/30">
                                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className="text-white font-bold truncate">{videoInfo.title.substring(0, 60)}{videoInfo.title.length > 60 && "..."}</h4>
                                                        <span className="text-sm text-gray-400">{videoInfo.channel}</span>
                                                    </div>
                                                    <div className={`px-3 py-1 rounded-full text-xs font-bold ${videoInfo.hasTranscript ? "bg-green-500/20 text-green-500 border border-green-500/30" : "bg-yellow-500/20 text-yellow-500 border border-yellow-500/30"}`}>
                                                        {videoInfo.hasTranscript ? "✓ Transkript verfügbar" : "⚠ Automatisches Transkript"}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </section>

                                    {/* Thema & Kontext */}
                                    <section>
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="bg-red-500/10 w-10 h-10 rounded-xl flex items-center justify-center text-red-500 flex-shrink-0">
                                                <BookOpen size={20} />
                                            </div>
                                            <h3 className="text-xl font-bold text-white">Thema & Kontext</h3>
                                        </div>
                                        <div className="space-y-6">
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-300 mb-2">
                                                    Unterrichtsthema *
                                                    <span className="block text-xs text-gray-500 font-normal mt-1">
                                                        Wichtig für präzise Aufgaben basierend auf dem Video
                                                    </span>
                                                </label>
                                                <input
                                                    value={topic}
                                                    onChange={(e) => setTopic(e.target.value)}
                                                    placeholder="z. B. 'Fünf Säulen des Islam', 'Ramadan', 'Propheten'"
                                                    className="w-full px-4 py-3 bg-gray-900/50 border-2 border-gray-800 rounded-xl text-white focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/30"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-300 mb-2">
                                                    Zusätzlicher Kontext
                                                    <span className="block text-xs text-gray-500 font-normal mt-1">
                                                        Optional: Was sollen Schüler aus dem Video lernen?
                                                    </span>
                                                </label>
                                                <textarea
                                                    value={context}
                                                    onChange={(e) => setContext(e.target.value)}
                                                    placeholder="z. B. 'Schwerpunkt auf historischem Kontext', 'Ethische Diskussion', 'Vergleich mit anderen Religionen'"
                                                    className="w-full px-4 py-3 bg-gray-900/50 border-2 border-gray-800 rounded-xl text-white focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/30 min-h-[100px] resize-y"
                                                />
                                            </div>
                                        </div>
                                    </section>

                                    {/* Dateiformat */}
                                    <section>
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="bg-red-500/10 w-10 h-10 rounded-xl flex items-center justify-center text-red-500 flex-shrink-0">
                                                <FileType size={20} />
                                            </div>
                                            <h3 className="text-xl font-bold text-white">Dateiformat</h3>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                            <button
                                                className={`p-4 border-2 rounded-xl flex items-center gap-4 transition-all ${fileFormat === "docx" ? "border-red-500 bg-red-500/10" : "border-gray-700 bg-gray-800/30 hover:border-gray-600"}`}
                                                onClick={() => setFileFormat("docx")}
                                            >
                                                <FileText size={18} className="text-gray-400 flex-shrink-0" />
                                                <div className="text-left">
                                                    <div className="font-bold text-white">Word (DOCX)</div>
                                                    <div className="text-sm text-gray-400">Bearbeitbar, ideal für Anpassungen</div>
                                                </div>
                                            </button>
                                            <button
                                                className={`p-4 border-2 rounded-xl flex items-center gap-4 transition-all ${fileFormat === "pdf" ? "border-red-500 bg-red-500/10" : "border-gray-700 bg-gray-800/30 hover:border-gray-600"}`}
                                                onClick={() => setFileFormat("pdf")}
                                            >
                                                <FileDown size={18} className="text-gray-400 flex-shrink-0" />
                                                <div className="text-left">
                                                    <div className="font-bold text-white">PDF</div>
                                                    <div className="text-sm text-gray-400">Druckfertig, universell kompatibel</div>
                                                </div>
                                            </button>
                                        </div>
                                        <div className="text-sm text-gray-400 p-4 bg-gray-900/30 rounded-xl">
                                            {fileFormat === "docx" ? (
                                                <span>📝 Bearbeitbares Format für individuelle Anpassungen</span>
                                            ) : (
                                                <span>📄 Druckfertiges Format für direkten Einsatz im Unterricht</span>
                                            )}
                                        </div>
                                    </section>

                                    {/* Schwierigkeitsgrad */}
                                    <section>
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="bg-red-500/10 w-10 h-10 rounded-xl flex items-center justify-center text-red-500 flex-shrink-0">
                                                <TrendingUp size={20} />
                                            </div>
                                            <h3 className="text-xl font-bold text-white">Schwierigkeitsgrad</h3>
                                        </div>
                                        <div className="flex flex-col sm:flex-row gap-3 mb-4">
                                            {["leicht", "mittel", "schwer"].map((level) => (
                                                <button
                                                    key={level}
                                                    className={`flex-1 py-3 px-4 border-2 rounded-xl font-semibold transition-all ${difficulty === level ? "border-red-500 bg-red-500/10 text-red-500" : "border-gray-700 bg-gray-800/30 text-gray-400 hover:border-gray-600"}`}
                                                    onClick={() => setDifficulty(level)}
                                                >
                                                    {level === "leicht" && "👶 Leicht"}
                                                    {level === "mittel" && "🎯 Mittel"}
                                                    {level === "schwer" && "🧠 Schwer"}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="text-sm text-gray-400 p-4 bg-gray-900/30 rounded-xl">
                                            {difficulty === "leicht" && (
                                                <span>Einfache Fragen, direkte Informationen aus dem Video</span>
                                            )}
                                            {difficulty === "mittel" && (
                                                <span>Anspruchsvolle Fragen, Verständnis und Zusammenfassung</span>
                                            )}
                                            {difficulty === "schwer" && (
                                                <span>Komplexe Fragen, Analyse und kritische Bewertung</span>
                                            )}
                                        </div>
                                    </section>

                                    {/* Aufgabentypen */}
                                    <section>
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="bg-red-500/10 w-10 h-10 rounded-xl flex items-center justify-center text-red-500 flex-shrink-0">
                                                <Target size={20} />
                                            </div>
                                            <h3 className="text-xl font-bold text-white">Aufgabentypen & Anzahl</h3>
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                            <TaskTypeCard
                                                type="singleChoice"
                                                label="Single Choice"
                                                description="Eine richtige Antwort aus 4 Optionen"
                                                value={numSingle}
                                                onChange={setNumSingle}
                                                color="#4CAF50"
                                                icon="🔘"
                                            />
                                            <TaskTypeCard
                                                type="multipleChoice"
                                                label="Multiple Choice"
                                                description="Mehrere richtige Antworten möglich"
                                                value={numMultiple}
                                                onChange={setNumMultiple}
                                                color="#2196F3"
                                                icon="☑️"
                                            />
                                            <TaskTypeCard
                                                type="freeText"
                                                label="Freie Antwort"
                                                description="Eigene Formulierungen der Schüler"
                                                value={numFree}
                                                onChange={setNumFree}
                                                color="#8b5cf6"
                                                icon="📝"
                                            />
                                            <TaskTypeCard
                                                type="gapText"
                                                label="Lückentext"
                                                description="Text mit mehreren Lücken zum Ausfüllen"
                                                value={numGap}
                                                onChange={setNumGap}
                                                color="#f59e0b"
                                                icon="🔤"
                                            />
                                        </div>
                                        <div className="flex justify-between items-center p-4 bg-gray-900/30 rounded-xl">
                                            <span className="text-gray-300 font-semibold">Gesamtaufgaben:</span>
                                            <span className="text-3xl font-black text-red-500">
                                                {numSingle + numMultiple + numFree + numGap}
                                            </span>
                                        </div>
                                    </section>
                                </div>
                            ) : activeTab === "preview" ? (
                                renderPreview()
                            ) : (
                                renderVideoInfo()
                            )}
                        </div>
                    </div>
                </div>

                {/* RIGHT PANEL */}
                <div className="space-y-6">
                    {/* Action Card */}
                    <div className="bg-gray-900/80 backdrop-blur-lg border border-gray-800 rounded-2xl p-6">
                        <h3 className="text-xl font-bold text-white mb-6">Generierung starten</h3>

                        <div className="space-y-4 mb-6 p-4 bg-gray-900/30 rounded-xl">
                            <div className="flex justify-between">
                                <span className="text-gray-400">Thema:</span>
                                <strong className="text-white">{topic || "Nicht angegeben"}</strong>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Format:</span>
                                <strong className={`px-3 py-1 rounded-full text-xs font-bold ${fileFormat === "docx" ? "bg-blue-500/20 text-blue-500 border border-blue-500/30" : "bg-red-500/20 text-red-500 border border-red-500/30"}`}>
                                    {fileFormat.toUpperCase()}
                                </strong>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Schwierigkeit:</span>
                                <strong className="text-white">{difficulty === "leicht" ? "Leicht" : difficulty === "mittel" ? "Mittel" : "Schwer"}</strong>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Aufgaben:</span>
                                <strong className="text-white">{numSingle + numMultiple + numFree + numGap}</strong>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Video:</span>
                                <strong className={`${videoInfo ? 'text-green-500' : 'text-red-500'}`}>
                                    {videoInfo ? "✓ Ausgewählt" : "❌ Fehlt"}
                                </strong>
                            </div>
                        </div>

                        <button
                            onClick={generateTasks}
                            disabled={loading || !topic.trim() || !youtubeUrl.trim()}
                            className={`w-full py-4 px-6 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-3 ${loading ? "opacity-80 cursor-wait" : ""} ${!topic.trim() || !youtubeUrl.trim() ? "opacity-50 cursor-not-allowed" : "bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 hover:-translate-y-1 hover:shadow-2xl hover:shadow-red-500/40"}`}
                        >
                            {loading ? (
                                <>
                                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    Analysiere Video & generiere {fileFormat.toUpperCase()}...
                                </>
                            ) : (
                                <>
                                    <Youtube size={20} />
                                    Video analysieren & {fileFormat.toUpperCase()} generieren
                                </>
                            )}
                        </button>

                        {(!topic.trim() || !youtubeUrl.trim()) && (
                            <div className="text-red-500 text-sm text-center mt-3">
                                {!topic.trim() && !youtubeUrl.trim()
                                    ? "Bitte Thema und YouTube-URL angeben."
                                    : !topic.trim()
                                        ? "Bitte ein Thema angeben."
                                        : "Bitte eine YouTube-URL eingeben."}
                            </div>
                        )}

                        <button
                            onClick={resetToDefaults}
                            className="w-full mt-4 py-3 px-4 border-2 border-gray-700 rounded-xl text-gray-400 font-semibold hover:border-red-500 hover:text-red-500 transition-all flex items-center justify-center gap-2"
                        >
                            <Settings size={16} />
                            Zurücksetzen
                        </button>
                    </div>

                    {/* Debug Info */}
                    {debugInfo && (
                        <div className="bg-gray-900/80 backdrop-blur-lg border border-gray-800 rounded-2xl p-5">
                            <div className="flex justify-between items-center mb-3">
                                <span className="text-sm font-semibold text-gray-400">Status</span>
                            </div>
                            <div className="text-sm break-words">
                                {debugInfo.includes("✅") ? (
                                    <span className="text-green-500 font-semibold">{debugInfo}</span>
                                ) : debugInfo.includes("❌") ? (
                                    <span className="text-red-500 font-semibold">{debugInfo}</span>
                                ) : (
                                    <span className="text-gray-300">{debugInfo}</span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Tips */}
                    <div className="bg-gradient-to-br from-red-500/10 to-red-400/10 border border-red-500/20 rounded-2xl p-6">
                        <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-3">
                            <Sparkles size={18} className="text-red-500" />
                            Tipps für YouTube-Aufgaben
                        </h4>
                        <ul className="space-y-3 text-gray-300">
                            <li className="flex items-start gap-2">
                                <span className="text-red-500">•</span>
                                <span className="text-sm">Kurze Videos (5-15 Min.) funktionieren am besten</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-red-500">•</span>
                                <span className="text-sm">Videos mit deutschen Untertiteln liefern bessere Ergebnisse</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-red-500">•</span>
                                <span className="text-sm">Dokumentationen und Erklärvideos sind ideal</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-red-500">•</span>
                                <span className="text-sm">Generierung kann bei langen Videos länger dauern</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* LOADING OVERLAY */}
            {loading && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-gradient-to-br from-gray-900 to-gray-950 p-8 rounded-3xl text-center max-w-md w-full mx-4 border border-gray-800 animate-fade-in">
                        <div className="w-16 h-16 border-4 border-gray-700 border-t-red-500 rounded-full animate-spin mx-auto mb-6" />
                        <h3 className="text-2xl font-bold text-white mb-3">KI analysiert YouTube-Video</h3>
                        <p className="text-gray-400 mb-6">
                            Video wird verarbeitet und Aufgaben werden generiert...
                            Dies kann 20-45 Sekunden dauern.
                        </p>
                        <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-red-500 to-red-600 w-1/2 animate-pulse" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}